/**
 * Meta Pixel — single source of truth for Facebook / Instagram Ads tracking.
 *
 * Env-driven: pass `REACT_APP_META_PIXEL_ID=<15-16 digit id>` at build time.
 * If the env var is missing, the file is a no-op — the app runs unaffected.
 *
 * Public API
 * ----------
 *   <MetaPixelRouterTracker />  — mount once at the app root. Injects the
 *                                  base pixel script AND fires PageView on
 *                                  every SPA route change.
 *   trackRegistration({...})    — call from the sign-up success handler.
 *   trackPurchase({...})        — call from the Stripe success page once
 *                                  payment_status === 'paid'.
 *   trackEvent(name, params?)   — thin escape hatch for custom events.
 *
 * All calls are safe to call before the pixel is loaded — fbq queues them.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';

let _scriptInjected = false;

function _ensurePixelLoaded() {
  if (!PIXEL_ID) return false;
  if (_scriptInjected) return true;
  _scriptInjected = true;
  // Standard Meta base pixel snippet — kept verbatim from the Meta
  // Events Manager install page so future updates paste in cleanly.
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0';
    n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', PIXEL_ID);
  return true;
}

export function trackEvent(name, params) {
  if (!_ensurePixelLoaded()) return;
  try {
    if (params) window.fbq('track', name, params);
    else window.fbq('track', name);
  } catch { /* pixel offline — non-fatal */ }
}

export function trackRegistration({ method = 'email' } = {}) {
  trackEvent('CompleteRegistration', { content_name: method });
}

export function trackPurchase({ value, currency = 'USD', contentIds = [], contentCategory = 'subscription' } = {}) {
  const params = { currency, content_type: 'product', content_category: contentCategory };
  if (typeof value === 'number' && !Number.isNaN(value)) params.value = value;
  if (contentIds.length) params.content_ids = contentIds;
  trackEvent('Purchase', params);
}

/**
 * Mount once at the app root. Fires PageView on the initial render AND
 * on every subsequent route change (SPA navigation).
 */
export default function MetaPixelRouterTracker() {
  const loc = useLocation();
  const firstRef = useRef(true);

  useEffect(() => {
    if (!PIXEL_ID) return;
    _ensurePixelLoaded();
    // First mount: send PageView + let subsequent nav also fire it.
    try { window.fbq('track', 'PageView'); } catch { /* ignore */ }
    firstRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  return null;
}
